import React from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  alpha,
  Button,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
  TokenOutlined,
  WorkspacePremiumOutlined,
  WarningAmberOutlined,
  TourOutlined,
} from "@mui/icons-material";
import ProfileComp from "./ProfileComp";
import { UserContext } from "../../context/UserContext";
import { useBackgroundJobs } from "../../context/BackgroundJobsContext";
import { mediaSrc } from "../../utils/mediaSrc";
import SyncOutlined from "@mui/icons-material/SyncOutlined";
import moment from "moment";
import {
  resolveTourIdForPage,
  useProductTourOptional,
} from "../../common/tours";

const TopBar = ({
  lang,
  theme,
  isMobile,
  isDark,
  selectedMenu,
  toggleColorMode,
  handleDrawerToggle,
  web,
  onGoBilling,
}) => {
  const { userData } = React.useContext(UserContext);
  const { activeCount } = useBackgroundJobs();
  const productTour = useProductTourOptional();
  const pageTourId = resolveTourIdForPage(selectedMenu);

  const startPageTour = () => {
    if (!productTour || !pageTourId) return;
    productTour.restartTour(pageTourId);
  };

  const plan = React.useMemo(() => {
    if (!userData?.plan) return null;
    try {
      return typeof userData.plan === "string"
        ? JSON.parse(userData.plan)
        : userData.plan;
    } catch {
      return null;
    }
  }, [userData?.plan]);

  const credits = Number(userData?.credits || 0);
  const maxCredits = Number(plan?.credits || 0);
  const expiryDate = userData?.plan_ending
    ? moment(userData.plan_ending)
    : null;
  const daysLeft = expiryDate ? expiryDate.diff(moment(), "days") : null;
  const isExpired = daysLeft !== null && daysLeft < 0;
  const isExpiringSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 5;
  const isLowCredits = maxCredits > 0 && credits > 0 && (credits / maxCredits) * 100 <= 20;
  const noCredits = credits <= 0;

  const pageLabel =
    (selectedMenu === "influencers" && (lang?.imageStudioNav || "Image Studio")) ||
    (selectedMenu === "ugc-ads-workflow" && lang?.ugcAdsWorkflow) ||
    (selectedMenu === "video-agent" && lang?.videoAgentNav) ||
    (selectedMenu === "b-roll-studio" && lang?.videoAgentNav) ||
    (selectedMenu === "makeugc" && lang?.makeUgcNav) ||
    (selectedMenu === "music" && lang?.musicNav) ||
    (selectedMenu === "actors" && lang?.actorsNav) ||
    (selectedMenu === "voices" && lang?.voicesNav) ||
    (selectedMenu === "billing" && (lang?.billingNav || "Billing")) ||
    selectedMenu
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      {/* ── Mobile AppBar ── */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            {web?.site_logo ? (
              <Box
                component="img"
                src={mediaSrc(web.site_logo)}
                alt={web?.site_name || "Logo"}
                sx={{ height: 26, objectFit: "contain" }}
              />
            ) : (
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: "1rem" }}
              >
                {web?.site_name || "App"}
              </Typography>
            )}

            <Stack direction="row" alignItems="center" spacing={1}>
              {activeCount > 0 && (
                <Chip
                  icon={<SyncOutlined sx={{ fontSize: "12px !important" }} />}
                  label={
                    activeCount === 1
                      ? lang?.backgroundJobRunningOne || "1 job running"
                      : (lang?.backgroundJobRunningMany || "{count} jobs running").replace(
                          "{count}",
                          String(activeCount),
                        )
                  }
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: "info.main",
                  }}
                />
              )}
              <Tooltip
                title={
                  noCredits
                    ? lang?.billingNoCreditsTooltip || "Open Billing to add credits"
                    : maxCredits > 0
                      ? `${credits.toLocaleString()} / ${maxCredits.toLocaleString()} ${lang?.credits || "credits"}`
                      : `${credits.toLocaleString()} ${lang?.credits || "credits"}`
                }
              >
                <Chip
                  icon={<TokenOutlined sx={{ fontSize: "12px !important" }} />}
                  label={credits.toLocaleString()}
                  size="small"
                  data-tour="topbar-credits"
                  onClick={noCredits && onGoBilling ? onGoBilling : undefined}
                  clickable={Boolean(noCredits && onGoBilling)}
                  sx={{
                    height: 24,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: noCredits
                      ? alpha(theme.palette.error.main, 0.12)
                      : isLowCredits
                        ? alpha(theme.palette.error.main, 0.1)
                        : alpha(theme.palette.warning.main, 0.1),
                    color: noCredits || isLowCredits ? "error.main" : "warning.main",
                    border: "none",
                    "& .MuiChip-icon": {
                      color: noCredits || isLowCredits ? "error.main" : "warning.main",
                    },
                  }}
                />
              </Tooltip>
              {noCredits && onGoBilling && (
                <Button variant="contained" size="small" sx={{ minWidth: 0, px: 1.2, py: 0.25 }} onClick={onGoBilling}>
                  {lang?.billingSubscribe || "Subscribe"}
                </Button>
              )}
              <IconButton onClick={toggleColorMode} size="small">
                {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Stack>
          </Toolbar>
        </AppBar>
      )}

      {/* ── Desktop Top Bar ── */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 0,
          }}
        >
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HomeIcon
              sx={{ fontSize: 20, color: theme.palette.text.secondary }}
            />
            <ChevronRightIcon
              sx={{ fontSize: 18, color: theme.palette.text.disabled }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                textTransform: "capitalize",
              }}
            >
              {pageLabel}
            </Typography>
          </Box>

          {/* ── Right Side ── */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {activeCount > 0 && (
              <Tooltip
                title={
                  lang?.backgroundJobRunningTooltip ||
                  "Jobs keep running while you browse — we'll notify you when they finish."
                }
              >
                <Chip
                  icon={<SyncOutlined sx={{ fontSize: "13px !important" }} />}
                  label={
                    activeCount === 1
                      ? lang?.backgroundJobRunningOne || "1 job running"
                      : (lang?.backgroundJobRunningMany || "{count} jobs running").replace(
                          "{count}",
                          String(activeCount),
                        )
                  }
                  size="small"
                  sx={{
                    height: 26,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: "info.main",
                    border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                    "& .MuiChip-icon": { color: "info.main" },
                  }}
                />
              </Tooltip>
            )}
            {/* Expiry warning badge */}
            {(isExpired || isExpiringSoon) && (
              <Tooltip
                title={
                  isExpired
                    ? lang?.billingNoCredits ||
                      "No active plan period — buy more credits anytime to keep using features."
                    : `${lang?.planExpires || "Plan expires"} ${expiryDate.fromNow()}`
                }
              >
                <Chip
                  icon={
                    <WarningAmberOutlined
                      sx={{ fontSize: "13px !important" }}
                    />
                  }
                  label={
                    isExpired ? lang?.expired || "Expired" : `${daysLeft}d left`
                  }
                  size="small"
                  sx={{
                    height: 26,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    cursor: "default",
                    bgcolor: isExpired
                      ? alpha(theme.palette.error.main, 0.1)
                      : alpha(theme.palette.warning.main, 0.1),
                    color: isExpired ? "error.main" : "warning.main",
                    border: `1px solid ${
                      isExpired
                        ? alpha(theme.palette.error.main, 0.25)
                        : alpha(theme.palette.warning.main, 0.25)
                    }`,
                    "& .MuiChip-icon": {
                      color: isExpired ? "error.main" : "warning.main",
                    },
                  }}
                />
              </Tooltip>
            )}

            {/* Plan badge */}
            {plan?.title && (
              <Tooltip title={`${plan.title} plan`}>
                <Chip
                  icon={
                    <WorkspacePremiumOutlined
                      sx={{ fontSize: "13px !important" }}
                    />
                  }
                  label={plan.title}
                  size="small"
                  sx={{
                    height: 26,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    cursor: "default",
                    bgcolor: alpha("#8B5CF6", 0.08),
                    color: "#8B5CF6",
                    border: `1px solid ${alpha("#8B5CF6", 0.2)}`,
                    "& .MuiChip-icon": { color: "#8B5CF6" },
                  }}
                />
              </Tooltip>
            )}

            {/* Credits pill */}
            <Tooltip
              title={
                noCredits
                  ? lang?.billingNoCreditsTooltip || "Open Billing to add credits"
                  : maxCredits > 0
                    ? `${credits.toLocaleString()} / ${maxCredits.toLocaleString()} ${lang?.credits || "credits remaining"}`
                    : `${credits.toLocaleString()} ${lang?.credits || "credits"}`
              }
            >
              <Chip
                icon={<TokenOutlined sx={{ fontSize: "13px !important" }} />}
                label={credits.toLocaleString()}
                size="small"
                data-tour="topbar-credits"
                onClick={noCredits && onGoBilling ? onGoBilling : undefined}
                clickable={Boolean(noCredits && onGoBilling)}
                sx={{
                  height: 26,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  cursor: noCredits && onGoBilling ? "pointer" : "default",
                  bgcolor: noCredits
                    ? alpha(theme.palette.error.main, 0.12)
                    : isLowCredits
                      ? alpha(theme.palette.error.main, 0.08)
                      : alpha(theme.palette.warning.main, 0.08),
                  color: noCredits || isLowCredits ? "error.main" : "warning.main",
                  border: `1px solid ${
                    noCredits || isLowCredits
                      ? alpha(theme.palette.error.main, 0.25)
                      : alpha(theme.palette.warning.main, 0.2)
                  }`,
                  "& .MuiChip-icon": {
                    color: noCredits || isLowCredits ? "error.main" : "warning.main",
                  },
                }}
              />
            </Tooltip>
            {noCredits && onGoBilling && (
              <Button variant="contained" size="small" sx={{ borderRadius: 2, textTransform: "none" }} onClick={onGoBilling}>
                {lang?.billingSubscribe || "Subscribe"}
              </Button>
            )}

            {productTour && pageTourId ? (
              <Tooltip title={lang?.tourRestartTooltip || "Take a tour of this page"}>
                <IconButton
                  data-tour="topbar-tour"
                  onClick={startPageTour}
                  disabled={productTour.isRunning}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <TourOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}

            {/* Dark mode toggle */}
            <IconButton
              onClick={toggleColorMode}
              sx={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>

            <ProfileComp theme={theme} lang={lang} />
          </Stack>
        </Box>
      )}
    </>
  );
};

export default TopBar;
